// src/config/siteConfig.ts
// Configuration centralisée du site pour faciliter l'ajout de contenu

export interface SubCategory {
  id: string;
  title: string;
  path: string;
  description?: string;
  count?: number;
  author?: string;
  status?: 'available' | 'soon' | 'draft';
  icon?: string;
}

export interface Category {
  id: string;
  title: string;
  titleAr?: string;
  description: string;
  icon: string;
  path?: string;
  subcategories: SubCategory[];
  status?: 'available' | 'soon' | 'draft';
  color?: string;
}

export interface SiteConfig {
  siteName: string;
  siteNameAr: string;
  author: {
    name: string;
    nameAr: string;
    credentials: string[];
    social: {
      youtube?: string;
      twitter?: string;
      facebook?: string;
      instagram?: string;
    };
  };
  categories: Category[];
}

export const siteConfig: SiteConfig = {
  siteName: "Mâhî Bâyân",
  siteNameAr: "",
  author: {
    name: "Mâhî Bâyân",
    nameAr: "ماهي بيان",
    credentials: [
      "Diplômé de l'institut de langue arabe de l'Université Islamique de Médine",
      "Étudié jusqu'au dernier niveau du Centre de langue arabe El-Ibânah",
      "Licence de la Faculté de Langue Arabe (Bac + 4)",
      "Grand Diplôme Supérieur en pratique et maîtrise de la langue arabe (Bac + 5)"
    ],
    social: {
      youtube: "https://www.youtube.com/channel/UCqGKAj8SQkTGQztS-h_MIjw",
      twitter: "https://x.com/Mahi_Bayan"
    }
  },
  categories: [
    {
      id: "articles",
      title: "Articles",
      titleAr: "المقالات",
      description: "Articles et enseignements islamiques traduits et rédigés",
      icon: "📚",
      color: "#7c3aed",
      status: "available",
      subcategories: [
        {
          id: "articles-all",
          title: "Tous les Articles",
          path: "/articles",
          description: "Collection complète d'articles islamiques",
          count: 3,
          author: "Mâhî Bâyân",
          status: "available"
        },
      ]
    },
    {
      id: "guides",
      title: "Guides Pratiques",
      titleAr: "الأدلة العملية",
      description: "Guides détaillés pour les actes d'adoration selon la Sunnah",
      icon: "🕋",
      color: "#059669",
      status: "available",
      subcategories: [
        {
          id: "guide-umra",
          title: "Guide de la 'Umra",
          path: "/guide-umra",
          description: "Guide complet pour accomplir la 'Umra selon la Sunnah du Prophète ﷺ",
          author: "Cheykh Souleymâne ar-Rouheylī",
          status: "available",
          icon: "🕋"
        },
        {
          id: "guide-hajj",
          title: "Guide du Ḥajj",
          path: "/guide-hajj",
          description: "Guide complet pour accomplir le Ḥajj selon la Sunnah du Prophète ﷺ",
          author: "Cheykh Souleymâne ar-Rouheylī",
          status: "available",
          icon: "🕌"
        },
      ]
    },
    {
      id: "ruqya",
      title: "Ruqya & Guérison",
      titleAr: "الرقية والشفاء",
      description: "Traitement islamique et invocations de guérison authentiques",
      icon: "🛡️",
      color: "#f59e0b",
      status: "available",
      subcategories: [
        {
          id: "ruqya-sorcellerie",
          title: "Contre la Sorcellerie",
          path: "/ruqya-sorcellerie",
          description: "Traitement contre la sorcellerie selon Cheykh Ibn Bâz",
          author: "Cheykh Ibn Bâz",
          status: "available"
        },
        {
          id: "recommandations-epreuve",
          title: "Conseils pour celui qui est éprouvé",
          path: "/recommandations-epreuve",
          description: "Recommandations pour celui touché par la sorcellerie, le mauvais œil ou la maladie",
          author: "Cheykh Souleymâne El-Rouheylî",
          status: "available"
        },
      ]
    },
    {
      id: "invocations",
      title: "Invocations",
      titleAr: "الأذكار",
      description: "Collection d'invocations authentiques traduites et expliquées",
      icon: "🤲",
      color: "#3b82f6",
      status: "available",
      subcategories: [
        {
          id: "invocations-soucis",
          title: "Contre les Soucis et la Tristesse",
          path: "/invocations-soucis",
          description: "Invocations prophétiques pour apaiser le cœur",
          count: 9,
          author: "Cheykh Souleymâne El-Rouheyli",
          status: "available"
        },
      ]
    },
    {
      id: "fatwa",
      title: "Fatâwâ",
      titleAr: "الفتاوى",
      description: "Avis juridiques des savants sur diverses questions",
      icon: "📜",
      color: "#dc2626",
      status: "available",
      subcategories: [
        {
          id: "fatwa-zakat",
          title: "La Zakât",
          path: "/fatwa/zakat",
          description: "100 Questions-Réponses sur l'aumône obligatoire",
          count: 104,
          author: "Cheykh Souleymâne El-Rouheyli",
          status: "available"
        },
      ]
    },
  ]
};

// Fonctions utilitaires
export const getCategoryById = (id: string): Category | undefined => {
  return siteConfig.categories.find(cat => cat.id === id);
};

export const getSubcategoryByPath = (path: string): SubCategory | undefined => {
  for (const category of siteConfig.categories) {
    const subcategory = category.subcategories.find(sub => sub.path === path);
    if (subcategory) return subcategory;
  }
  return undefined;
};

export const getAvailableCategories = (): Category[] => {
  return siteConfig.categories.filter(cat => cat.status !== 'draft');
};

export const getAvailableSubcategories = (categoryId: string): SubCategory[] => {
  const category = getCategoryById(categoryId);
  if (!category) return [];
  return category.subcategories.filter(sub => sub.status === 'available');
};